import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useEducatorProfileFacetDescriptor } from "@/components/search/educator-profile-facet"
import {
  type TermFacetDescriptor,
  useTermFacetDescriptor,
} from "@/components/search/facet-descriptor"
import { FACET_IDS } from "@/components/search/facet-ids"
import { createAttributeOptions } from "@/constants/speciality"

const CURRICULUM_FACET = {
  type: "terms",
  field: "class_details.curriculum_key",
  size: 1000,
}
const SUBJECT_FACET = {
  type: "terms",
  field: "class_details.subject_key",
  size: 1000,
}
const SPECIALITIES_FACET = {
  type: "terms",
  field: "class_details.speciality_attributes.keyword",
  size: 1000,
}
const TAGS_FIELD = "profile_details.tags.keyword"

/**
 * The class facets, described once for the public sidebar and for the
 * exported shortlist module, so both offer the same options and labels.
 */
export function useClassFacetDescriptors(): TermFacetDescriptor[] {
  return [
    useClassCurriculumFacetDescriptor(),
    useClassSubjectFacetDescriptor(),
    useClassSpecialitiesFacetDescriptor(),
    useClassTagsFacetDescriptor(),
  ]
}

export function useClassCurriculumFacetDescriptor(): TermFacetDescriptor {
  const { t } = useTranslation("search")
  const { t: tCurricula } = useTranslation("subjects", {
    keyPrefix: "curricula",
  })

  return useTermFacetDescriptor({
    id: FACET_IDS.curriculum,
    config: CURRICULUM_FACET,
    label: t("facet.curriculum"),
    pillLabel: (value) =>
      `${t("result.curriculum")} ${tCurricula(value as never)}`,
    searchable: true,
    option: (value, count) => ({
      value,
      count,
      label: tCurricula(value as never),
    }),
  })
}

export function useClassSubjectFacetDescriptor(): TermFacetDescriptor {
  const { t } = useTranslation("search")
  const { t: tSubjects } = useTranslation("subjects", { keyPrefix: "subjects" })

  return useTermFacetDescriptor({
    id: FACET_IDS.subject,
    config: SUBJECT_FACET,
    label: t("facet.subject"),
    pillLabel: (value) => `${t("result.subject")} ${tSubjects(value as never)}`,
    searchable: true,
    option: (value, count) => ({
      value,
      count,
      label: tSubjects(value as never),
    }),
  })
}

export function useClassSpecialitiesFacetDescriptor(): TermFacetDescriptor {
  const { t } = useTranslation("search")
  const attributeOptions = useMemo(
    () => createAttributeOptions("search:select_attributes"),
    [],
  )
  const translate = useMemo(
    () => (name: string) =>
      attributeOptions.find((option) => option.value === name)?.label ?? name,
    [attributeOptions],
  )

  return useTermFacetDescriptor({
    id: FACET_IDS.specialities,
    config: SPECIALITIES_FACET,
    label: t("facet.specialities"),
    pillLabel: (value) => `${t("result.speciality")} ${translate(value)}`,
    searchable: true,
    option: (value, count) => ({ value, count, label: translate(value) }),
  })
}

export function useClassTagsFacetDescriptor(): TermFacetDescriptor {
  return useEducatorProfileFacetDescriptor({
    id: FACET_IDS.tags,
    field: TAGS_FIELD,
  })
}
